import { TabStyles } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { L10n } from '~src/commonInfra/L10n/L10n';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

import { Messages } from '../../../codegen/Localize';
import { cssClass } from '../../../style';
import { fetchCapacityInfoForInfoBar, fetchSiteScopedCapacityInfoForInfoBar } from '../../../userManagement/services/licenses/LicensesService';
import { ILicenseConsumptionInfo } from '../../serverApi/types/ILicenseConsumptionInfo';

export interface LicenseInfoBarProps {
  readonly hasSiteLicensingInfo: boolean;
}

export function LicenseInfoBar(props: LicenseInfoBarProps) {
  const [isFetching, setIsFetching] = React.useState(true);
  const [licenseInfo, setLicenseInfo] = React.useState<ILicenseConsumptionInfo>(undefined);
  const [isCoreBasedLicense, setIsCoreBasedLicense] = React.useState(false);

  React.useEffect(
    () => {
      let didUnmount = false;
      const fetch = async () => {
        const licenseInfoPromise = props.hasSiteLicensingInfo ?
          fetchSiteScopedCapacityInfoForInfoBar() :
          fetchCapacityInfoForInfoBar();
        const licenseInfo = await licenseInfoPromise;
        if (!didUnmount) {
          setIsFetching(false);
          setLicenseInfo(licenseInfo);
          setIsCoreBasedLicense(licenseInfo.totalCoreLicenses > 0);
        }
      };

      fetch();

      return () => {
        didUnmount = true;
      };
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={LicenseInfoBarContainer} data-tb-test-id='license-info-bar'>
      { !isFetching &&
      <>
        <span className={LicenseInfoBarItem}>
          {Messages.licenseCreatorCounts({
            USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedCreatorUsers),
            TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalCreatorUsers)
          })}
        </span>
        <span className={LicenseInfoBarItem}>
          {isCoreBasedLicense ? Messages.licenseExplorerUnlimited() :
            Messages.licenseExplorerCounts({
            USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedExplorerUsers),
            TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalExplorerUsers)
          })}
        </span>
        <span className={LicenseInfoBarItem}>
          {isCoreBasedLicense ? Messages.licenseViewerUnlimited() :
            Messages.licenseViewerCounts({
            USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedViewerUsers),
            TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalViewerUsers)
          })}
        </span>
        <span className={LicenseInfoBarItem}>
          {Messages.unlicensedCount({
            COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalUnlicensedUsers)
          })}
        </span>
      </>}
    </div>
  );
}

const cssPrefix = 'LicenseInfoBar';
const LicenseInfoBarContainer = cssClass(cssPrefix, 'container', {
  height: '2em',
  overflowY: 'hidden',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingTop: '4px',
  $nest: {
    '&>*': {
      paddingRight: TabStyles.Spacing.MediumSpace,
      paddingLeft: TabStyles.Spacing.MediumSpace,
    },
    '&>*:last-child': {
      paddingRight: 0,
    },
    '&>*:first-child': {
      paddingLeft: 0,
    }
  }
});

const LicenseInfoBarItem = cssClass(cssPrefix, 'item', {
  minWidth: 0, // 0 is the default
  color: CommonStyles.Colors.Grey102,
  fontSize: '14px',
});
